import { Cancel01Icon } from '@hugeicons/core-free-icons';
import type {
  HostConfig,
  JakeWidgetApi,
  JakeWidgetIdentity,
  JakeWidgetOptions,
  MessengerToHostMessage,
  SessionResponse,
} from '../shared/contracts';
import { incomingMessagePreview, type IncomingMessagePreview } from './incoming-message-preview';
import { consumeConversationResumeFragment } from './resume-code';

declare global {
  interface Window {
    Jake?: JakeWidgetApi;
    JakeWidget?: JakeWidgetApi;
  }
}

type HugeIconData = ReadonlyArray<readonly [string, Readonly<Record<string, string | number>>]>;

const svgNamespace = 'http://www.w3.org/2000/svg';
const formatPreviewTime = (sentAt: string): string => {
  const parsed = Date.parse(sentAt);
  const date = Number.isNaN(parsed) ? new Date() : new Date(parsed);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};
const takeConversationResumeCode = (): string | null => {
  const resume = consumeConversationResumeFragment(window.location.href);
  if (!resume) return null;
  window.history.replaceState(window.history.state, '', resume.cleanedUrl);
  return resume.code;
};

const createHugeIcon = (icon: HugeIconData, className: string): SVGSVGElement => {
  const svg = document.createElementNS(svgNamespace, 'svg');
  svg.classList.add(className);
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');

  for (const [tag, attributes] of icon) {
    const element = document.createElementNS(svgNamespace, tag);
    for (const [name, value] of Object.entries(attributes)) {
      if (name === 'key') continue;
      element.setAttribute(
        name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`),
        String(value),
      );
    }
    svg.append(element);
  }

  return svg;
};

const launcherPetals: readonly string[] = [
  'M317.094 362.349C352.085 397.34 390.581 415.575 403.078 403.078C415.575 390.581 397.34 352.085 362.349 317.094C327.358 282.103 288.861 263.868 276.365 276.365C263.868 288.861 282.103 327.358 317.094 362.349Z',
  'M149.651 317.094C114.66 352.085 96.425 390.581 108.922 403.078C121.419 415.575 159.915 397.34 194.906 362.349C229.897 327.358 248.132 288.861 235.635 276.365C223.139 263.868 184.642 282.103 149.651 317.094Z',
  'M194.906 149.651C159.915 114.66 121.419 96.425 108.922 108.922C96.425 121.419 114.66 159.915 149.651 194.906C184.642 229.897 223.139 248.132 235.635 235.635C248.132 223.139 229.897 184.642 194.906 149.651Z',
  'M362.349 194.906C397.34 159.915 415.575 121.419 403.078 108.922C390.581 96.425 352.085 114.66 317.094 149.651C282.103 184.642 263.868 223.139 276.365 235.635C288.861 248.132 327.358 229.897 362.349 194.906Z',
  'M416 280C437.208 280 454.4 269.255 454.4 256C454.4 242.745 437.208 232 416 232C394.792 232 377.6 242.745 377.6 256C377.6 269.255 394.792 280 416 280Z',
  'M232 416C232 437.208 242.745 454.4 256 454.4C269.255 454.4 280 437.208 280 416C280 394.792 269.255 377.6 256 377.6C242.745 377.6 232 394.792 232 416Z',
  'M96 232C74.7923 232 57.6 242.745 57.6 256C57.6 269.255 74.7923 280 96 280C117.208 280 134.4 269.255 134.4 256C134.4 242.745 117.208 232 96 232Z',
  'M280 96C280 74.7923 269.255 57.6 256 57.6C242.745 57.6 232 74.7923 232 96C232 117.208 242.745 134.4 256 134.4C269.255 134.4 280 117.208 280 96Z',
];

// The Jake mark's petal glyph. The launcher button itself supplies the
// squircle and the workspace accent, so the glyph is drawn in currentColor.
const createLauncherMark = (): SVGSVGElement => {
  const svg = document.createElementNS(svgNamespace, 'svg');
  svg.classList.add('launcher-mark');
  svg.setAttribute('viewBox', '0 0 512 512');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');

  for (const definition of launcherPetals) {
    const petal = document.createElementNS(svgNamespace, 'path');
    petal.classList.add('launcher-mark-petal');
    petal.setAttribute('d', definition);
    svg.append(petal);
  }

  return svg;
};

const launcherForeground = (hex: string): '#ffffff' | '#18181b' => {
  const channels = [1, 3, 5].map((index) => Number.parseInt(hex.slice(index, index + 2), 16) / 255);
  const luminance = channels
    .map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index]!, 0);
  return luminance > 0.48 ? '#18181b' : '#ffffff';
};

const applyLauncherAccent = (layer: HTMLElement, accent: string): void => {
  layer.style.setProperty('--jake-accent', accent);
  layer.style.setProperty('--jake-accent-foreground', launcherForeground(accent));
};

const styles = `
  :host { all: initial; }
  *, *::before, *::after { box-sizing: border-box; }
  .jake-layer {
    --jake-accent: #18181b;
    --jake-accent-foreground: #ffffff;
    position: fixed;
    right: max(20px, env(safe-area-inset-right));
    bottom: max(20px, env(safe-area-inset-bottom));
    z-index: 2147483000;
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "SF Pro Text", Inter, "Segoe UI", sans-serif;
  }
  .jake-layer[data-position="left"] {
    right: auto;
    left: max(20px, env(safe-area-inset-left));
  }
  .jake-launcher {
    position: relative;
    display: grid;
    width: 56px;
    height: 56px;
    place-items: center;
    overflow: visible;
    border: 1px solid color-mix(in srgb, var(--jake-accent-foreground) 14%, var(--jake-accent));
    border-radius: 14px;
    color: var(--jake-accent-foreground);
    background: var(--jake-accent);
    box-shadow: 0 18px 46px -14px rgba(0, 0, 0, .38), 0 3px 10px rgba(0, 0, 0, .14), inset 0 1px 0 color-mix(in srgb, var(--jake-accent-foreground) 10%, transparent);
    cursor: pointer;
    transition: transform 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
    animation: jake-launch-arrive 620ms cubic-bezier(.2,.82,.24,1.18) 120ms backwards;
    -webkit-tap-highlight-color: transparent;
  }
  @keyframes jake-launch-arrive {
    0% { opacity: 0; transform: translateY(18px) scale(.72) rotate(-7deg); }
    66% { opacity: 1; transform: translateY(-3px) scale(1.035) rotate(1deg); }
    100% { opacity: 1; transform: translateY(0) scale(1) rotate(0); }
  }
  .jake-launcher:hover { background: color-mix(in srgb, var(--jake-accent) 90%, var(--jake-accent-foreground)); transform: translateY(-2px); box-shadow: 0 22px 54px -16px rgba(0, 0, 0, .44), 0 4px 12px rgba(0, 0, 0, .16), inset 0 1px 0 color-mix(in srgb, var(--jake-accent-foreground) 13%, transparent); }
  .jake-launcher:active { transform: translateY(0) scale(.97); }
  .jake-launcher:focus-visible { outline: 2px solid var(--jake-accent); outline-offset: 4px; }
  .jake-launcher svg { width: 22px; height: 22px; transition: transform 180ms ease, opacity 140ms ease; }
  .jake-launcher::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(120% 100% at 30% 0%, color-mix(in srgb, #fff 26%, transparent), transparent 62%);
    pointer-events: none;
  }
  .jake-launcher .launcher-mark { position: relative; width: 43px; height: 43px; transition: transform 180ms ease, opacity 140ms ease; }
  .launcher-mark-petal { fill: currentColor; }
  .jake-launcher .close-icon { position: absolute; opacity: 0; transform: rotate(-70deg) scale(.75); }
  .jake-layer[data-open="true"] .launcher-mark { opacity: 0; transform: rotate(10deg) scale(.72); }
  .jake-layer[data-open="true"] .close-icon { opacity: 1; transform: rotate(0) scale(1); }
  .jake-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    display: none;
    min-width: 21px;
    height: 21px;
    padding: 0 6px;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    border-radius: 999px;
    color: #fff;
    background: #e48678;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
  }
  .jake-badge[data-visible="true"] { display: flex; }
  .jake-notification {
    position: absolute;
    right: 0;
    bottom: 72px;
    display: flex;
    width: min(300px, calc(100vw - 92px));
    align-items: flex-start;
    gap: 10px;
    padding: 11px 13px;
    border: 1px solid #3e3e46;
    border-radius: 12px 12px 4px 12px;
    color: #f4f4f5;
    background: #1d1d21;
    box-shadow: 0 18px 48px -18px rgba(0, 0, 0, .5), 0 4px 12px rgba(0, 0, 0, .2);
    opacity: 0;
    visibility: hidden;
    transform: translateY(8px) scale(.98);
    transform-origin: bottom right;
    pointer-events: none;
    cursor: pointer;
    text-align: left;
    transition: opacity 160ms ease, transform 180ms cubic-bezier(.2,.75,.25,1), visibility 160ms;
    -webkit-tap-highlight-color: transparent;
  }
  .jake-notification::after {
    content: "";
    position: absolute;
    right: 20px;
    bottom: -5px;
    width: 9px;
    height: 9px;
    border-right: 1px solid #3e3e46;
    border-bottom: 1px solid #3e3e46;
    background: #1d1d21;
    transform: rotate(45deg);
  }
  .jake-notification:hover { border-color: #52525b; background: #222226; }
  .jake-notification:hover::after { border-color: #52525b; background: #222226; }
  .jake-notification:focus-visible { outline: 2px solid var(--jake-accent); outline-offset: 3px; }
  .jake-notification-avatar {
    display: grid;
    width: 28px;
    height: 28px;
    flex: none;
    place-items: center;
    overflow: hidden;
    margin-top: 1px;
    border: 1px solid #3e3e46;
    border-radius: 50%;
    color: #f4f4f5;
    background: #2a2a30;
    font-size: 12px;
    font-weight: 650;
    line-height: 1;
  }
  .jake-notification-avatar img { display: block; width: 100%; height: 100%; object-fit: cover; }
  .jake-notification-body {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
  }
  .jake-notification-meta {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 6px;
  }
  .jake-notification-author {
    overflow: hidden;
    color: #a1a1aa;
    font-size: 10px;
    font-weight: 650;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .jake-notification-time {
    flex: none;
    color: #71717a;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
  }
  .jake-notification-text {
    display: -webkit-box;
    overflow: hidden;
    color: #f4f4f5;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow-wrap: anywhere;
  }
  .jake-notification-dismiss {
    position: absolute;
    top: -8px;
    right: -8px;
    display: grid;
    width: 22px;
    height: 22px;
    place-items: center;
    border: 1px solid #3e3e46;
    border-radius: 50%;
    color: #a1a1aa;
    background: #2a2a30;
    box-shadow: 0 4px 12px rgba(0, 0, 0, .3);
    cursor: pointer;
    opacity: 0;
    transition: opacity 140ms ease, color 140ms ease, background-color 140ms ease;
    -webkit-tap-highlight-color: transparent;
  }
  .jake-notification-dismiss svg { width: 11px; height: 11px; }
  .jake-notification:hover .jake-notification-dismiss,
  .jake-notification-dismiss:focus-visible { opacity: 1; }
  .jake-notification-dismiss:hover { color: #f4f4f5; background: #35353c; }
  .jake-notification-dismiss:focus-visible { outline: 2px solid var(--jake-accent); outline-offset: 2px; }
  .jake-layer[data-preview="true"][data-open="false"] .jake-notification {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
  .jake-panel {
    position: absolute;
    right: 0;
    bottom: 72px;
    width: min(400px, calc(100vw - 32px));
    height: min(650px, calc(100dvh - 108px));
    overflow: hidden;
    border: 0;
    border-radius: 18px;
    background: transparent;
    box-shadow: 0 30px 90px -32px rgba(0, 0, 0, .45), 0 12px 28px rgba(0, 0, 0, .14);
    opacity: 0;
    visibility: hidden;
    transform: translateY(12px) scale(.98);
    transform-origin: bottom right;
    pointer-events: none;
    transition: opacity 180ms ease, transform 200ms cubic-bezier(.2,.75,.25,1), visibility 180ms, width 220ms cubic-bezier(.2,.75,.25,1), height 220ms cubic-bezier(.2,.75,.25,1);
  }
  .jake-layer[data-layout="article"] .jake-panel {
    width: min(780px, calc(100vw - 32px));
    height: min(760px, calc(100dvh - 108px));
  }
  .jake-layer[data-position="left"] .jake-panel { right: auto; left: 0; transform-origin: bottom left; }
  .jake-layer[data-position="left"] .jake-notification {
    right: auto;
    left: 0;
    border-radius: 12px 12px 12px 4px;
    transform-origin: bottom left;
  }
  .jake-layer[data-position="left"] .jake-notification::after { right: auto; left: 20px; }
  .jake-layer[data-open="true"] .jake-panel { opacity: 1; visibility: visible; transform: translateY(0) scale(1); pointer-events: auto; }
  .jake-frame { display: block; width: 100%; height: 100%; border: 0; color-scheme: dark; background: transparent; }
  .jake-layer[data-hide-launcher="true"] .jake-launcher,
  .jake-layer[data-hide-launcher="true"] .jake-notification { display: none; }
  @media (prefers-reduced-motion: reduce) {
    .jake-launcher, .jake-launcher svg, .jake-notification, .jake-panel { animation: none; transition: none; }
  }
  @media (max-width: 560px) {
    .jake-layer, .jake-layer[data-position="left"] { right: max(14px, env(safe-area-inset-right)); bottom: max(14px, env(safe-area-inset-bottom)); left: auto; }
    .jake-panel, .jake-layer[data-position="left"] .jake-panel,
    .jake-layer[data-layout="article"] .jake-panel {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100dvh;
      border: 0;
      border-radius: 0;
      transform: translateY(14px);
    }
    .jake-layer[data-open="true"] .jake-launcher { opacity: 0; visibility: hidden; pointer-events: none; }
  }
`;

class WidgetController implements JakeWidgetApi {
  private options: JakeWidgetOptions | null = null;
  private host: HTMLDivElement | null = null;
  private layer: HTMLDivElement | null = null;
  private frame: HTMLIFrameElement | null = null;
  private launcher: HTMLButtonElement | null = null;
  private badge: HTMLSpanElement | null = null;
  private notification: HTMLDivElement | null = null;
  private notificationAvatar: HTMLSpanElement | null = null;
  private notificationAuthor: HTMLSpanElement | null = null;
  private notificationTime: HTMLSpanElement | null = null;
  private notificationText: HTMLSpanElement | null = null;
  private notificationTimer: number | null = null;
  private open = false;
  private ready = false;
  private identity: JakeWidgetIdentity | null = null;
  private identityPending = false;
  private tokenError: string | null = null;
  private pendingDraft: string | null = null;
  private refreshTimer: number | null = null;
  private ephemeralGuestId: string | null = null;
  private resumeCode: string | null = null;
  private readonly listeners = new Map<string, Set<(payload?: unknown) => void>>();
  private readonly receiveMessage = (event: MessageEvent<unknown>) => this.onMessage(event);

  boot(options: JakeWidgetOptions): void {
    if (!options.publicKey?.trim()) throw new Error('Jake Widget requires a public key');
    this.shutdown();
    this.resumeCode = takeConversationResumeCode();
    this.options = {
      ...options,
      publicKey: options.publicKey.trim(),
      baseUrl: (options.baseUrl || this.detectBaseUrl()).replace(/\/$/, ''),
      locale: options.locale || navigator.language || 'en',
      position: options.position || 'right',
    };
    this.pendingDraft =
      typeof options.initialDraft === 'string' && options.initialDraft.trim()
        ? options.initialDraft
        : null;
    this.identity = options.identity ?? null;
    this.identityPending =
      !this.identity && !this.resumeCode && Boolean(this.options.tokenEndpoint);
    this.mount();
    window.addEventListener('message', this.receiveMessage);
    if (!this.identity && !this.resumeCode && this.options.tokenEndpoint) void this.fetchIdentity();
    if (this.resumeCode) this.show();
  }

  show(): void {
    if (!this.layer || !this.options) return;
    this.open = true;
    this.layer.dataset.open = 'true';
    this.launcher?.setAttribute('aria-expanded', 'true');
    this.setUnread(0);
    this.launcher?.setAttribute('aria-label', 'Close support chat');
    this.post({ source: 'jake-widget-host', type: 'visibility', open: true });
    window.setTimeout(() => this.frame?.focus(), 210);
    this.emit('open');
  }

  hide(): void {
    if (!this.layer) return;
    this.open = false;
    this.layer.dataset.open = 'false';
    this.launcher?.setAttribute('aria-expanded', 'false');
    this.launcher?.setAttribute('aria-label', 'Open support chat');
    this.post({ source: 'jake-widget-host', type: 'visibility', open: false });
    this.launcher?.focus();
    this.emit('close');
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  setDraft(text: string): void {
    this.pendingDraft = text;
    if (this.ready) this.post({ source: 'jake-widget-host', type: 'message.draft', text });
  }

  send(text: string): void {
    const normalized = text.trim();
    if (!normalized) return;
    this.post({ source: 'jake-widget-host', type: 'message.send', text: normalized });
  }

  identify(identity: JakeWidgetIdentity): void {
    if (!identity.userId.trim() || !identity.token.trim()) {
      throw new Error('Jake Widget identify requires a userId and token');
    }
    this.identity = identity;
    this.tokenError = null;
    this.scheduleRefresh(identity.expiresIn);
    this.post({
      source: 'jake-widget-host',
      type: 'identify',
      token: identity.token,
      refreshToken: identity.refreshToken,
      expiresIn: identity.expiresIn,
      userId: identity.userId,
    });
  }

  shutdown(): void {
    this.post({ source: 'jake-widget-host', type: 'shutdown' });
    window.removeEventListener('message', this.receiveMessage);
    this.host?.remove();
    this.host = null;
    this.layer = null;
    this.frame = null;
    this.launcher = null;
    this.badge = null;
    this.notification = null;
    this.notificationAvatar = null;
    this.notificationAuthor = null;
    this.notificationTime = null;
    this.notificationText = null;
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
    this.notificationTimer = null;
    this.options = null;
    this.open = false;
    this.ready = false;
    this.identity = null;
    this.identityPending = false;
    this.tokenError = null;
    this.pendingDraft = null;
    if (this.refreshTimer !== null) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.ephemeralGuestId = null;
    this.resumeCode = null;
  }

  on(event: string, listener: (payload?: unknown) => void): () => void {
    const listeners = this.listeners.get(event) ?? new Set();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    return () => listeners.delete(listener);
  }

  private mount(): void {
    if (!this.options) return;
    const host = document.createElement('div');
    host.id = 'jake-widget-root';
    const shadow = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = styles;
    shadow.append(style);

    const layer = document.createElement('div');
    layer.className = 'jake-layer';
    layer.dataset.open = 'false';
    layer.dataset.preview = 'false';
    layer.dataset.layout = 'default';
    layer.dataset.position = this.options.position;
    layer.dataset.hideLauncher = String(Boolean(this.options.hideDefaultLauncher));
    applyLauncherAccent(layer, this.options.accent || '#18181b');

    const panel = document.createElement('div');
    panel.className = 'jake-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Jake support conversation');

    const frame = document.createElement('iframe');
    frame.className = 'jake-frame';
    frame.title = 'Jake support';
    frame.allow = 'clipboard-write';
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.src = this.frameUrl();
    panel.append(frame);

    const launcher = document.createElement('button');
    launcher.className = 'jake-launcher';
    launcher.type = 'button';
    launcher.setAttribute('aria-label', 'Open support chat');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.append(createLauncherMark(), createHugeIcon(Cancel01Icon, 'close-icon'));
    const badge = document.createElement('span');
    badge.className = 'jake-badge';
    badge.dataset.visible = 'false';
    badge.setAttribute('aria-live', 'polite');
    launcher.append(badge);
    launcher.addEventListener('click', () => this.toggle());

    const notification = document.createElement('div');
    notification.className = 'jake-notification';
    notification.setAttribute('role', 'button');
    notification.tabIndex = 0;
    notification.setAttribute('aria-label', 'Open new support reply');
    const notificationAvatar = document.createElement('span');
    notificationAvatar.className = 'jake-notification-avatar';
    notificationAvatar.setAttribute('aria-hidden', 'true');
    const notificationBody = document.createElement('span');
    notificationBody.className = 'jake-notification-body';
    const notificationMeta = document.createElement('span');
    notificationMeta.className = 'jake-notification-meta';
    const notificationAuthor = document.createElement('span');
    notificationAuthor.className = 'jake-notification-author';
    const notificationTime = document.createElement('span');
    notificationTime.className = 'jake-notification-time';
    const notificationText = document.createElement('span');
    notificationText.className = 'jake-notification-text';
    notificationMeta.append(notificationAuthor, notificationTime);
    notificationBody.append(notificationMeta, notificationText);
    const notificationDismiss = document.createElement('button');
    notificationDismiss.className = 'jake-notification-dismiss';
    notificationDismiss.type = 'button';
    notificationDismiss.setAttribute('aria-label', 'Dismiss message preview');
    notificationDismiss.append(createHugeIcon(Cancel01Icon, 'dismiss-icon'));
    notificationDismiss.addEventListener('click', (event) => {
      event.stopPropagation();
      this.clearNotificationPreview();
    });
    notification.append(notificationAvatar, notificationBody, notificationDismiss);
    notification.addEventListener('click', () => this.show());
    notification.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.show();
    });

    layer.append(panel, notification, launcher);
    shadow.append(layer);
    document.body.append(host);

    this.host = host;
    this.layer = layer;
    this.frame = frame;
    this.launcher = launcher;
    this.badge = badge;
    this.notification = notification;
    this.notificationAvatar = notificationAvatar;
    this.notificationAuthor = notificationAuthor;
    this.notificationTime = notificationTime;
    this.notificationText = notificationText;
  }

  private frameUrl(): string {
    const options = this.options!;
    const url = new URL('/messenger', options.baseUrl);
    url.searchParams.set('public_key', options.publicKey);
    url.searchParams.set('parent_origin', window.location.origin);
    if (options.workspaceId) url.searchParams.set('workspace_id', options.workspaceId);
    return url.toString();
  }

  private hostConfig(): HostConfig {
    const options = this.options!;
    const identity = this.identity;
    const resumeCode = this.resumeCode;
    this.resumeCode = null;
    return {
      publicKey: options.publicKey,
      workspaceId: options.workspaceId,
      parentOrigin: window.location.origin,
      guestId: this.guestId(options.publicKey),
      locale: options.locale!,
      page: { title: document.title, url: window.location.href },
      theme: { accent: options.accent, position: options.position },
      ...(identity
        ? {
            sessionToken: identity.token,
            sessionRefreshToken: identity.refreshToken,
            sessionExpiresIn: identity.expiresIn,
            userId: identity.userId,
          }
        : {}),
      authenticationRequired: Boolean(options.tokenEndpoint),
      ...(this.tokenError ? { authenticationError: this.tokenError } : {}),
      ...(resumeCode ? { resumeCode } : {}),
    };
  }

  private async fetchIdentity(): Promise<void> {
    if (!this.options?.tokenEndpoint) return;
    this.identityPending = true;
    try {
      const response = await fetch(this.options.tokenEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'x-jake-public-key': this.options.publicKey,
        },
        body: JSON.stringify({
          publicKey: this.options.publicKey,
          page: { title: document.title, url: window.location.href },
        }),
      });
      if (!response.ok) throw new Error(`Token endpoint returned ${response.status}`);
      const session = (await response.json()) as SessionResponse;
      if (!session.token || !session.userId)
        throw new Error('Token endpoint response is incomplete');
      this.identify({
        token: session.token,
        refreshToken: session.refreshToken,
        userId: session.userId,
        expiresIn: session.expiresIn,
      });
      this.tokenError = null;
    } catch (error) {
      this.tokenError = error instanceof Error ? error.message : 'Customer authentication failed';
      this.emit('error', { message: this.tokenError });
    }
    this.identityPending = false;
    if (this.ready)
      this.post({ source: 'jake-widget-host', type: 'configure', config: this.hostConfig() });
  }

  private onMessage(event: MessageEvent<unknown>): void {
    if (!this.options || !this.frame || event.source !== this.frame.contentWindow) return;
    if (event.origin !== new URL(this.options.baseUrl!).origin) return;
    const message = event.data as Partial<MessengerToHostMessage>;
    if (message.source !== 'jake-widget-messenger' || !message.type) return;

    switch (message.type) {
      case 'ready':
        this.ready = true;
        if (!this.identityPending) {
          this.post({ source: 'jake-widget-host', type: 'configure', config: this.hostConfig() });
        }
        this.post({ source: 'jake-widget-host', type: 'visibility', open: this.open });
        if (this.pendingDraft !== null) {
          this.post({
            source: 'jake-widget-host',
            type: 'message.draft',
            text: this.pendingDraft,
          });
        }
        break;
      case 'close':
        this.hide();
        break;
      case 'unread':
        this.setUnread(typeof message.count === 'number' ? message.count : 0);
        this.emit('unread', { count: message.count });
        break;
      case 'theme':
        if (typeof message.accent === 'string' && /^#[0-9a-f]{6}$/i.test(message.accent)) {
          if (this.layer) applyLauncherAccent(this.layer, message.accent);
        }
        break;
      case 'layout':
        if (this.layer)
          this.layer.dataset.layout = message.mode === 'article' ? 'article' : 'default';
        break;
      case 'event':
        if (message.name === 'message.received') {
          const preview = incomingMessagePreview(message.payload);
          if (preview && !this.open) this.setNotificationPreview(preview);
        }
        if (message.name) this.emit(message.name, message.payload);
        break;
      case 'error':
        this.emit('error', { message: message.message });
        break;
    }
  }

  private post(message: unknown): void {
    if (!this.ready || !this.options || !this.frame?.contentWindow) return;
    this.frame.contentWindow.postMessage(message, new URL(this.options.baseUrl!).origin);
  }

  private setUnread(count: number): void {
    if (!this.badge) return;
    const safe = Math.max(0, Math.floor(count));
    if (safe === 0) this.clearNotificationPreview();
    this.badge.textContent = safe > 99 ? '99+' : String(safe);
    this.badge.dataset.visible = String(safe > 0 && !this.open);
    this.launcher?.setAttribute(
      'aria-label',
      safe > 0
        ? `Open support chat, ${safe} unread ${safe === 1 ? 'message' : 'messages'}`
        : 'Open support chat',
    );
  }

  private setNotificationPreview(preview: IncomingMessagePreview): void {
    if (
      !this.layer ||
      !this.notification ||
      !this.notificationAvatar ||
      !this.notificationAuthor ||
      !this.notificationTime ||
      !this.notificationText
    )
      return;
    if (this.open) return;
    this.notificationAvatar.replaceChildren();
    if (preview.avatarUrl) {
      const image = document.createElement('img');
      image.src = preview.avatarUrl;
      image.alt = '';
      image.referrerPolicy = 'no-referrer';
      this.notificationAvatar.append(image);
    } else {
      this.notificationAvatar.textContent = preview.authorName.charAt(0).toUpperCase();
    }
    this.notificationAuthor.textContent = preview.authorName;
    this.notificationTime.textContent = formatPreviewTime(preview.sentAt);
    this.notificationText.textContent = preview.text;
    this.notification.setAttribute(
      'aria-label',
      `Open support chat. ${preview.authorName}: ${preview.text}`,
    );
    this.layer.dataset.preview = 'true';
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
    this.notificationTimer = window.setTimeout(() => this.clearNotificationPreview(), 8_000);
  }

  private clearNotificationPreview(): void {
    if (
      !this.layer ||
      !this.notification ||
      !this.notificationAvatar ||
      !this.notificationAuthor ||
      !this.notificationTime ||
      !this.notificationText
    )
      return;
    if (this.notificationTimer !== null) window.clearTimeout(this.notificationTimer);
    this.notificationTimer = null;
    this.layer.dataset.preview = 'false';
    this.notificationAvatar.replaceChildren();
    this.notificationAuthor.textContent = '';
    this.notificationTime.textContent = '';
    this.notificationText.textContent = '';
    this.notification.setAttribute('aria-label', 'Open new support reply');
  }

  private emit(event: string, payload?: unknown): void {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }

  private scheduleRefresh(expiresIn?: number): void {
    if (this.refreshTimer !== null) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (!this.options?.tokenEndpoint || !expiresIn) return;
    const refreshIn = Math.max(30, expiresIn - 60) * 1_000;
    this.refreshTimer = window.setTimeout(() => void this.fetchIdentity(), refreshIn);
  }

  private guestId(publicKey: string): string {
    const key = `jake:guest:${publicKey}`;
    try {
      const existing = window.localStorage.getItem(key);
      if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
      const created = crypto.randomUUID();
      window.localStorage.setItem(key, created);
      return created;
    } catch {
      this.ephemeralGuestId ??= crypto.randomUUID();
      return this.ephemeralGuestId;
    }
  }

  private detectBaseUrl(): string {
    const script = document.currentScript as HTMLScriptElement | null;
    return script?.src ? new URL(script.src).origin : window.location.origin;
  }
}

const controller = new WidgetController();
window.Jake = controller;
window.JakeWidget = controller;

const script = document.currentScript as HTMLScriptElement | null;
const publicKey = script?.dataset.jakePublicKey || script?.dataset.publicKey;
if (publicKey) {
  controller.boot({
    publicKey,
    workspaceId: script?.dataset.jakeWorkspaceId,
    baseUrl: script?.dataset.jakeBaseUrl || (script?.src ? new URL(script.src).origin : undefined),
    tokenEndpoint: script?.dataset.jakeTokenEndpoint,
    locale: script?.dataset.jakeLocale,
    position: script?.dataset.jakePosition === 'left' ? 'left' : 'right',
    accent: script?.dataset.jakeAccent,
    hideDefaultLauncher: script?.dataset.jakeHideLauncher === 'true',
  });
}
